import { Store } from 'store/storeTypes';
import { HolidayBlock } from './Holidays.types';
import { daysBetweenInclusive, isoDate, parseIsoDate } from './utils/holidayDateUtils';

export const selectHolidaysIsModalOpen = (state: Store) => state.holidays.isModalOpen;
export const selectHolidaysDraft = (state: Store) => state.holidays.draft;
export const selectHolidaysCalendarYear = (state: Store) => state.holidays.calendarYear;
export const selectHolidaysCalendarMonth = (state: Store) => state.holidays.calendarMonth;
export const selectHolidaysDragAnchor = (state: Store) => state.holidays.dragAnchor;
export const selectHolidaysDragHover = (state: Store) => state.holidays.dragHover;
export const selectHolidaysPendingDeleteId = (state: Store) => state.holidays.pendingDeleteId;
export const selectHolidaysActiveSites = (state: Store) => state.holidays.activeSites;

export const selectHolidaysForSite = (state: Store, site: string) =>
  state.holidays.bySite[site] ?? [];

export type HolidaysSummary = {
  totalDays: number;
  next: { name: string; start: string; end: string } | null;
};

const shiftYears = (key: string, years: number) => {
  const date = parseIsoDate(key);
  return isoDate(date.getFullYear() + years, date.getMonth(), date.getDate());
};

// A past repeatYearly block counts as its next yearly occurrence.
const nextOccurrence = (block: HolidayBlock, todayKey: string): HolidayBlock | null => {
  if (block.end >= todayKey) return block;
  if (!block.repeat) return null;
  let years = Number(todayKey.slice(0, 4)) - Number(block.end.slice(0, 4));
  if (shiftYears(block.end, years) < todayKey) years += 1;
  return { ...block, start: shiftYears(block.start, years), end: shiftYears(block.end, years) };
};

// The API summary is aggregated across all requested sites, so the per-site one is computed locally.
export const selectHolidaysSummaryForSite = (state: Store, site: string): HolidaysSummary => {
  const holidays = selectHolidaysForSite(state, site);
  const totalDays = holidays.reduce((sum, b) => sum + daysBetweenInclusive(b.start, b.end), 0);
  const now = new Date();
  const todayKey = isoDate(now.getFullYear(), now.getMonth(), now.getDate());
  const upcoming = holidays
    .map(b => nextOccurrence(b, todayKey))
    .filter((b): b is HolidayBlock => b !== null)
    .sort((a, b) => a.start.localeCompare(b.start))[0];
  return {
    totalDays,
    next: upcoming
      ? { name: upcoming.name || 'Day off', start: upcoming.start, end: upcoming.end }
      : null,
  };
};
